import {
  Component,
  Inject,
  OnInit,
  Optional,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { Moment } from 'moment';
import * as moment from 'moment';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepicker,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import { MatSelect } from '@angular/material/select';
import { isPlatformBrowser } from '@angular/common';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';

const DATE_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'MMM DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMM YYYY',
  },
};

@Component({
  selector: 'app-buyflow-request-form-field-dialog',
  templateUrl: './buyflow-request-form-field-dialog.component.html',
  styleUrls: ['./buyflow-request-form-field-dialog.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy,
    },
  ],
})
export class BuyflowRequestFormFieldDialogComponent implements OnInit {
  @ViewChild('datepickerDate') datePickerDate: MatDatepicker<Date>;
  @ViewChild('rangePicker', { static: false })
  rangePicker: MatDateRangePicker<Moment>;
  @ViewChild('durationSelect') durationSelect: any;
  requestData: any;
  newStartTime: string;
  newPassengers: any;
  newDate: Moment;
  dateStart: Moment;
  dateEnd: Moment;
  newDuration: any;
  title: string;
  isMobile: boolean;
  isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional()
    private dialogRef: MatDialogRef<BuyflowRequestFormFieldDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional()
    private sheet: MatBottomSheetRef<BuyflowRequestFormFieldDialogComponent>,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public bottomSheetData: any,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    const sub = this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
    if (this.isMobile) {
      this.requestData = bottomSheetData;
    } else {
      this.requestData = data.requestData;
    }
  }

  ngOnInit(): void {
    switch (this.requestData.editField) {
      case 'duration':
        this.newDuration = this.requestData.currentValue;
        this.title = 'Duration';
        if (this.isBrowser) {
          setTimeout(() => {
            this.durationSelect.open();
          }, 300);
        }
        break;
      case 'passengers':
        this.newPassengers = this.requestData.currentValue;
        this.title = 'Passengers';
        break;
      case 'startTime':
        this.newStartTime = this.requestData.currentValue;
        this.title = 'Start Time';
        break;
      case 'date':
        this.title = 'Date';
        this.newDate = this.requestData.currentValue;
        if (this.isBrowser) {
          setTimeout(() => {
            document.getElementById('datePickerDate').focus();
          }, 400);
        }
        break;
      case 'dates':
        this.title = 'Dates';
        this.dateStart = this.requestData.dateStart;
        this.dateEnd = this.requestData.dateEnd;
        break;
    }
  }

  dateRangeFilter = (incomingMoment: Moment): boolean => {
    if (
      (incomingMoment && incomingMoment.isBefore(moment(), 'day')) ||
      !incomingMoment
    ) {
      return false;
    }

    return !this.requestData.vessel.calendarPreferences.some((calPref) => {
      let isAfter = incomingMoment.isSameOrAfter(
        moment(calPref.fromDate),
        'day',
      );
      let isBefore = incomingMoment.isSameOrBefore(
        moment(calPref.toDate),
        'day',
      );
      if (!calPref.available && isAfter && isBefore) {
        return true;
      }
    });
  };

  dateFilter = (incomingMoment: Moment): boolean => {
    if (
      (incomingMoment && incomingMoment.isBefore(moment(), 'day')) ||
      !incomingMoment
    ) {
      return false;
    }

    const advanceNoticeHours = this.getAdvanceNoticeHoursForCalendar();

    if (
      advanceNoticeHours &&
      advanceNoticeHours >= 24 &&
      incomingMoment &&
      incomingMoment.isBefore(moment().add(advanceNoticeHours, 'hours'))
    ) {
      return false;
    }

    if (
      this.requestData.vesselPrefs &&
      this.requestData.vesselPrefs.advanceNoticeMaxMonths > 0 &&
      incomingMoment &&
      incomingMoment.isAfter(
        moment().add(
          this.requestData.vesselPrefs.advanceNoticeMaxMonths,
          'months',
        ),
      )
    ) {
      return false;
    }

    if (
      this.requestData.buyflowProductOfferings &&
      this.requestData.vesselPrefs &&
      this.requestData.buyflowProductOfferings.length > 0 &&
      this.requestData.vesselPrefs.customAvailability
    ) {
      let daysArray = ['sun', 'mon', 'tues', 'wed', 'thurs', 'fri', 'sat'];
      let passesProductOfferings = false;

      this.requestData.buyflowProductOfferings.forEach((offering) => {
        let requestedDay = daysArray[incomingMoment?.day()];
        if (
          offering &&
          offering.active &&
          offering.days[requestedDay] !== false
        ) {
          passesProductOfferings = true;
        }
      });

      if (passesProductOfferings) {
        // let the function continue by doing nothing
      } else {
        return false;
      }
    }

    if (this.requestData.unavailables) {
      return !this.requestData.unavailables.some((unavailable) =>
        moment(incomingMoment)
          .set('hour', 12)
          .isBetween(unavailable.start, unavailable.end),
      );
    }

    if (incomingMoment && incomingMoment.isSame(moment(), 'day')) {
      return true;
    }
    return true;
  };

  private getAdvanceNoticeHoursForCalendar(): number {
    // if advanceNoticeHours is greater than or equal to a day, we'll want to subtract 24 hours because there will
    //  likely still be selectable times on that day
    if (this.requestData.vesselPrefs) {
      return this.requestData.vesselPrefs.advanceNoticeMinHours >= 24
        ? this.requestData.vesselPrefs.advanceNoticeMinHours - 24
        : this.requestData.vesselPrefs.advanceNoticeMinHours;
    }
  }

  emitChanges() {
    const result = {
      editField: this.requestData.editField,
      newStartTime: this.newStartTime,
      newPassengers: this.newPassengers,
      newDate: this.newDate,
      newDuration: this.newDuration,
      dateStart: this.dateStart,
      dateEnd: this.dateEnd,
    };
    if (this.isMobile) {
      this.sheet.dismiss(result);
    } else {
      this.dialogRef.close(result);
    }
  }

  closeModal() {
    if (this.isMobile) {
      this.sheet.dismiss();
    } else {
      this.dialogRef.close();
    }
  }

  openDatePicker($event: FocusEvent) {
    if (this.datePickerDate) {
      this.datePickerDate.open();
    }
  }
}
